import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import LockIcon from "@material-ui/icons/Lock";
import LockOpenIcon from "@material-ui/icons/LockOpen";
import * as APP from "../redux_store/appState";
import BSL_Select from "../components/BSL_Select";
import { updateJobParameter, updateBOMOrderData } from "../redux_store/jobState";
import { takeControl } from "../redux_store/jobStateHelper";
import BSL_Button from "../components/BSL_Button";
import WarningIcon from "@material-ui/icons/Warning";
import EmailIcon from "@material-ui/icons/Email";
import { API_BASE } from "../AppConfig";
import { fetchTimeout } from "../components/Common";
import { SHOW_DESIGNER_MESSAGE } from "../redux_store/jobState_Validation";
import { useState } from "react";
import BSL_Dialog from "../components/BSL_Dialog";
import { Badge, Box, Tooltip } from "@material-ui/core";
import { ErpEditorInternal } from "../components_main/ErpTable";
import { addStatusTrackingEntry } from "../helper/Helper";

const useStyles = makeStyles({
    root: {},
    orderLock: {
        position: "absolute",
        top: "86px",
        width: "100%",
        zIndex: "1",
        background: "#dedfe5",
        color: "black",
        padding: "10px 170px 10px 90px",
        display: "flex",
        zIndex: 55,
        opacity: 1,
        transition: "opacity 0.5s",
    },
    button: {
        marginLeft: "10px",
    },
});

export const unlock = (openMessageBox, app, job, dispatch, lockReason = null) => {
    const UNLOCKED = app.designer[APP.UNLOCKED];

    if (lockReason === "CUSTOMISED") {
        openMessageBox({
            title: "Unlock Job",
            message: [
                <>
                    This job is currently locked because it has a customised component list.
                    <br />
                    <br />
                    To make changes to the design, choose one of the following options:
                    <br />
                    <br />
                    UNLOCK & REMOVE: Removes the customised component list and unlocks the design.
                    <br />
                    UNLOCK & RETAIN: Keeps the customised component list, but design changes will only be reflected in
                    the system material list. The customised list will remain unchanged.
                </>,
            ],
            primaryActionText: "CANCEL",
            secondaryActionText: "UNLOCK & RETAIN",
            tertiaryActionText: "UNLOCK & REMOVE",
            secondaryAction: () => {
                dispatch(
                    updateJobParameter(null, {
                        AcceptedWarnings: job.AcceptedWarnings
                            ? [...job.AcceptedWarnings, "JobHasCustomList"]
                            : ["JobHasCustomList"],
                        LastUnlockAction: "RETAIN",
                    })
                );
            },
            tertiaryAction: () => {
                dispatch(updateJobParameter(null, { LastUnlockAction: "REMOVE" }));
                dispatch(updateBOMOrderData({}, null));
            },
        });
    } else if (app.user.Role >= 90) {
        openMessageBox({
            title: UNLOCKED ? "Lock Job" : "Unlock Job",
            message: [
                <>
                    WARNING: Unlocking the job may cause unintended changes to the BOM / Order List. By unlocking the
                    job you are accepting this risk and agree to thoroughly check through all order items before
                    confirming this order. Are you sure you would like to {UNLOCKED ? "lock" : "UNLOCK"} it?
                </>,
            ],
            primaryActionText: UNLOCKED ? "LOCK" : "UNLOCK",
            secondaryActionText: "CANCEL",
            primaryAction: () => {
                if (
                    (!UNLOCKED && !job?.AcceptedWarnings) ||
                    !job?.AcceptedWarnings.includes("UNLOCKED BY: " + app.user.DisplayName)
                )
                    dispatch(
                        updateJobParameter(null, {
                            AcceptedWarnings: job.AcceptedWarnings
                                ? [...job.AcceptedWarnings, "UNLOCKED BY: " + app.user.DisplayName]
                                : ["UNLOCKED BY: " + app.user.DisplayName],
                        })
                    );
                dispatch(APP.toggleDesigner(APP.UNLOCKED));
            },
        });
    }
};

export default function LockBar(props) {
    const { openMessageBox } = props;
    const classes = useStyles();
    const dispatch = useDispatch();

    const app = useSelector((state) => state.app);
    const job = useSelector((state) => state.job);
    const tool_drawer_width = useSelector((state) => state.app?.designer?.[APP.TOOL_DRAWER_WIDTH]);

    const right_padding = (tool_drawer_width && tool_drawer_width > 0 ? tool_drawer_width : 0) + 10;
    const acceptedWarnings = job?.AcceptedWarnings || [];
    const erpNumbers = job?.ErpNumbers || [];

    const [emailDialog, setEmailDialog] = useState(false);

    const takeControlConfirm = () => {
        openMessageBox({
            title: "Take Control of Job",
            message: [
                <>
                    Are you sure you would like to take control of editing this job? If you continue, the job will
                    update from the server.
                </>,
            ],
            primaryActionText: "TAKE CONTROL",
            secondaryActionText: "CANCEL",
            button1Id: "Confirm-Control-Job",
            primaryAction: () => {
                dispatch(APP.setDesignerLoadingBackdrop("REQUESTING CONTROL OF JOB #" + job.id));
                dispatch(takeControl(APP.UNLOCKED));
            },
        });
    };

    const read_review_notes = () => {
        let message = [];

        if (order_review_message) {
            let message_lines = order_review_message.split("\n");
            for (let str of message_lines)
                message.push(
                    <>
                        {str}
                        <br />
                    </>
                );
        } else {
            message.push(
                <>
                    {"*No Notes*"}
                    <br />
                </>
            );
        }
        openMessageBox({
            title: "Review Notes",
            message: message,
        });
    };

    const read_order_email = () => {
        let api_url = API_BASE() + "document/" + job.id + "/order_email";

        return fetchTimeout(
            api_url,
            {
                method: "GET",
            },
            20000
        )
            .then((response) => response.json())
            .then((json) => {
                if (json.Error) {
                    throw json.Error;
                } else {
                    setEmailDialog(json.HtmlPart);
                }
            })
            .catch((e) => {
                dispatch({
                    type: SHOW_DESIGNER_MESSAGE,
                    message: "Error: Customer Order Email Not Found On Server",
                });
            });
    };

    if (!job) return null;

    const UNLOCKED = app.designer[APP.UNLOCKED];
    const reviewed = job?.OrderStatus?.toUpperCase() === "REVIEWED";
    const accepted = job?.OrderStatus?.toUpperCase() === "ACCEPTED";
    const customListIsOpen = app?.designer?.[APP.CUSTOM_COMPONENT_LIST_IS_OPEN] ?? false;
    const userAcknowledgedCustomList = acceptedWarnings.includes("JobHasCustomList") ?? false;
    const hasCustomBOM = (job?.QuoteParameters?.BOM?.OrderList?.items?.length ?? 0) > 0 || customListIsOpen;

    let order_status = job?.OrderStatus?.toUpperCase() ?? "";
    let order_review_message = job?.OrderReviewMessage ?? "";

    const warning_messages = props.warningMessages || [];

    let order_bg_color = "#dedfe5";
    if (UNLOCKED) order_bg_color = "#73a04d";
    if (reviewed) order_bg_color = "darkorange";
    if (accepted) order_bg_color = "#008200";

    if (app.user.Role >= 90) {
        order_status = (
            <BSL_Select
                style={{
                    flex: "initial",
                    padding: "0",
                    margin: "0",
                    marginTop: "-5px",
                }}
                selectStyle={{
                    background: "none",
                    border: "none",
                    color: accepted ? "white" : "black",
                }}
                value={order_status}
                tight={true}
                items={["UNLOCK JOB", "PENDING", "REVIEWED", "ACCEPTED"]}
                onChange={(e) => {
                    if (e.target.value === "UNLOCK JOB") {
                        openMessageBox({
                            title: "REMOVE ORDER STATUS",
                            message: [
                                <>
                                    This job has been set to ORDERED. Are you sure you would like to remove its ORDER
                                    status?
                                </>,
                            ],
                            primaryActionText: "REMOVE ORDER STATUS",
                            secondaryActionText: "CANCEL",
                            primaryAction: () => {
                                const updatedOrderStatusTracking = addStatusTrackingEntry(
                                    "REMOVED",
                                    job?.OrderStatusTracking
                                );
                                dispatch(
                                    updateJobParameter(null, {
                                        OrderStatus: "",
                                        OrderStatusTracking: updatedOrderStatusTracking,
                                    })
                                );
                            },
                        });
                    } else {
                        const updatedOrderStatusTracking = addStatusTrackingEntry(
                            e.target.value,
                            job?.OrderStatusTracking
                        );
                        dispatch(
                            updateJobParameter(null, {
                                OrderStatus: e.target.value,
                                OrderStatusTracking: updatedOrderStatusTracking,
                            })
                        );
                    }
                }}
            />
        );
    }

    if (job?.App?.Locked) {
        return (
            <div
                className={classes.orderLock}
                style={{
                    background: "orange",
                    color: accepted ? "white" : "black",
                    paddingRight: right_padding + "px",
                }}
            >
                <div style={{ flex: 1, textAlign: "end" }}></div>
                <span
                    onClick={takeControlConfirm}
                    style={{
                        cursor: "pointer",
                        textTransform: "uppercase",
                    }}
                >
                    <LockIcon
                        fontSize="small"
                        style={{
                            verticalAlign: "middle",
                            width: "0.75em",
                            height: "0.75em",
                            marginRight: "5px",
                        }}
                    />
                    {job?.App?.LockMessage}
                    <BSL_Button id="Control-Button" tight className={classes.button} onClick={takeControlConfirm}>
                        TAKE CONTROL
                    </BSL_Button>
                </span>
                <div style={{ flex: 1, textAlign: "start", cursor: "pointer" }}></div>
            </div>
        );
    }

    let lockBar = null;

    lockBar = (
        <div
            className={classes.orderLock}
            style={{
                display: warning_messages.length > 0 || hasCustomBOM ? "flex" : "none",
                background: hasCustomBOM ? "rgba(128, 128, 128, 0.5)" : "rgba(255, 165, 0, 0.5)",
                color: accepted ? "white" : "black",
                paddingRight: `${right_padding}px`,
                opacity: warning_messages.length > 0 || hasCustomBOM ? 1 : 0,
                pointerEvents: warning_messages.length > 0 || hasCustomBOM ? "auto" : "none",
            }}
        >
            {warning_messages.length > 0 && !hasCustomBOM && (
                <div style={{ flex: 1, pointerEvents: "none" }} className={classes.warningMessages}>
                    {warning_messages.map((message, index) => (
                        <div style={{ textAlign: "center" }} key={index}>
                            {message}
                        </div>
                    ))}
                </div>
            )}
            {hasCustomBOM && (
                <div style={{ flex: 1, pointerEvents: "auto" }} className={classes.warningMessages}>
                    {userAcknowledgedCustomList ? (
                        <span style={{ textAlign: "center", cursor: "pointer" }}>
                            JOB HAS A CUSTOMISED COMPONENT LIST - CHANGES TO THE DESIGN WILL ONLY AFFECT THE SYSTEM
                            MATERIAL LIST
                        </span>
                    ) : (
                        <span
                            style={{ textAlign: "center", cursor: "pointer" }}
                            onClick={() => unlock(openMessageBox, app, job, dispatch, "CUSTOMISED")}
                        >
                            <LockOpenIcon
                                fontSize="small"
                                style={{
                                    verticalAlign: "middle",
                                    width: "0.75em",
                                    height: "0.75em",
                                    marginRight: "5px",
                                }}
                            />
                            JOB HAS A CUSTOMISED COMPONENT LIST - CLICK TO UNLOCK
                        </span>
                    )}
                </div>
            )}
        </div>
    );

    if (!job?.OrderStatus || job?.OrderStatus === "") return lockBar;

    let custom_list_warning = false;

    if (job.OrderListIsCustom && app.user.Role >= 90) {
        if (!job?.QuoteParameters?.BOM?.OrderList?.items?.length) {
            custom_list_warning = true;
        } else {
            if (job?.QuoteParameters?.BOM?.OrderList?.items?.length < 2) {
                custom_list_warning = true;
            }
        }
    }

    return (
        <>
            <div
                className={classes.orderLock}
                style={{
                    background: order_bg_color,
                    color: accepted ? "white" : "black",
                    paddingRight: right_padding + "px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                }}
            >
                <div style={{ display: "flex", flexDirection: "row" }}>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "row",
                            alignSelf: "center",
                            margin: "0 auto",
                        }}
                    >
                        <div style={{ flex: 1, textAlign: "end" }}>
                            ORDER STATUS{" "}
                            <span onClick={read_order_email} style={{ cursor: "pointer" }}>
                                <EmailIcon style={{ fontSize: "16px", verticalAlign: "bottom" }} />
                            </span>
                            :{" "}
                        </div>

                        {order_status}
                        {accepted && app.user.Role >= 90 && <> - AND SENT TO LYVES</>}
                        {accepted && app.user.Role < 90 && <> - AND PROCESSED</>}
                        {reviewed && (
                            <span onClick={read_review_notes} style={{ cursor: "pointer" }}>
                                ... READ REVIEW NOTES
                            </span>
                        )}
                        {!reviewed && (
                            <span
                                onClick={() => unlock(openMessageBox, app, job, dispatch)}
                                style={{ cursor: "pointer" }}
                            >
                                {UNLOCKED ? (
                                    <LockOpenIcon style={{ fontSize: "16px", verticalAlign: "bottom" }} />
                                ) : (
                                    <LockIcon style={{ fontSize: "16px", verticalAlign: "bottom" }} />
                                )}
                                EDITING IS {UNLOCKED ? "UNLOCKED" : "LOCKED"}
                            </span>
                        )}
                    </div>
                    {accepted && erpNumbers && (
                        <div
                            onClick={() => {
                                if (app?.user?.Role >= 90)
                                    openMessageBox({
                                        title: "ERP Order Numbers",
                                        message: [
                                            <div key="erp-editor">
                                                <ErpEditorInternal
                                                    onChange={(updated) =>
                                                        dispatch(
                                                            updateJobParameter(null, {
                                                                ErpNumbers: updated,
                                                            })
                                                        )
                                                    }
                                                    dispatch={dispatch}
                                                    prefix={job.Distributor.POA}
                                                    initialErpNumbers={erpNumbers}
                                                />
                                            </div>,
                                        ],
                                        primaryActionText: "CLOSE",
                                    });
                            }}
                        >
                            <Tooltip
                                title={
                                    <>
                                        {erpNumbers?.length > 0 &&
                                            erpNumbers.map((num, idx) => <div key={idx}>{num}</div>)}
                                    </>
                                }
                                arrow
                            >
                                <Box
                                    sx={{
                                        display: "inline-block",
                                        cursor: "pointer",
                                        color: "rgba(255,255,255,0.8)",
                                        transition: "color 0.2s ease",
                                        "&:hover": { color: "white" },
                                        marginRight: "10px",
                                    }}
                                >
                                    <Badge
                                        color="secondary"
                                        variant={erpNumbers?.length > 1 ? "dot" : undefined}
                                        overlap="rectangular"
                                        anchorOrigin={{
                                            vertical: "top",
                                            horizontal: "right",
                                        }}
                                    >
                                        <span>
                                            {erpNumbers?.length > 0
                                                ? `ERP Order Number: ${erpNumbers[0]}`
                                                : `Add ERP Order Number`}
                                        </span>
                                    </Badge>
                                </Box>
                            </Tooltip>
                        </div>
                    )}
                </div>
                {app.user.Role >= 90 && custom_list_warning && (
                    <div
                        style={{
                            position: "relative",
                            left: "50%",
                            right: "50%",
                            marginLeft: "-60vw",
                            marginRight: "-50vw",
                            width: "125vw",
                            background: "orange",
                            color: "black",
                            textAlign: "center",
                            padding: "6px 0",
                            marginBottom: "-10px",
                        }}
                    >
                        <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                            <WarningIcon
                                style={{
                                    width: "17px",
                                    height: "13px",
                                    color: "darkred",
                                }}
                            />
                            This order was made with a custom list but there is no Custom Component List in this job.
                            Please confirm.
                        </div>
                    </div>
                )}

                <BSL_Dialog
                    title={"Order Email Details"}
                    open={emailDialog}
                    onClose={() => setEmailDialog(false)}
                    button1Text="Close"
                    button1Click={() => setEmailDialog(false)}
                    htmlContent={emailDialog}
                    style={{ maxWidth: "initial", height: "95vh" }}
                    contentStyle={{ maxWidth: "initial", height: "95vh" }}
                    fullWidth={true}
                    maxWidth={"md"}
                ></BSL_Dialog>
            </div>
        </>
    );
}
